import { states } from '@buyerflo/core/schemas/generic'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation } from '@tanstack/react-query'
import { DatePicker } from 'components/datePicker'
import Input from 'components/input'
import CustomSelect, { Option } from 'components/select'
import { useShowSnack } from 'components/snackbar'
import { Controller, useForm } from 'react-hook-form'
import Button from 'components/button'
import { SizableText, XStack, YStack } from 'tamagui'
import { useTRPC } from 'utils/trpc'
import { clearStoredRef, getStoredRef } from 'utils/referral'
import { OrgDetailsSchema, type OrgDetailsFormData } from '../schemas'

export const OrgDetailsStep = () => {
    const trpc = useTRPC()
    const showSnack = useShowSnack()
    const createOrgMutation = useMutation(trpc.createOrg.createOrg.mutationOptions())

    const {
        control,
        handleSubmit,
        formState: { errors, isSubmitting },
    } = useForm<OrgDetailsFormData>({
        resolver: zodResolver(OrgDetailsSchema),
        defaultValues: {
            orgName: '',
            state: undefined,
            tradingName: '',
            abn: '',
            acn: '',
            licenceNumber: '',
            licenseeName: '',
            licenceExpiry: '',
            phone: '',
            mobileNumber: '',
            fax: '',
            orgEmail: '',
            website: '',
        },
    })

    const onSubmit = async (data: OrgDetailsFormData) => {
        try {
            const { checkoutUrl } = await createOrgMutation.mutateAsync({
                orgName: data.orgName,
                state: data.state,
                tradingName: data.tradingName,
                abn: data.abn,
                acn: data.acn || undefined,
                licenceNumber: data.licenceNumber,
                licenseeName: data.licenseeName || undefined,
                licenceExpiry: data.licenceExpiry || undefined,
                phone: data.phone,
                mobileNumber: data.mobileNumber || undefined,
                fax: data.fax || undefined,
                orgEmail: data.orgEmail,
                website: data.website || undefined,
                ref: getStoredRef(),
            })
            clearStoredRef()
            window.location.href = checkoutUrl
        } catch {
            showSnack('Failed to create organisation', 'danger')
        }
    }

    return (
        <form noValidate onSubmit={handleSubmit(onSubmit)}>
            <YStack gap="$4">
                <SizableText fontSize="$8" fontWeight="700" letterSpacing={-0.3}>
                    Set up your organisation
                </SizableText>
                <SizableText color="$gray10" fontSize="$4">
                    Tell us about your agency
                </SizableText>

                <Controller
                    name="orgName"
                    control={control}
                    render={({ field }) => (
                        <Input
                            label="Organisation Name *"
                            editMode
                            placeholder="My Agency"
                            value={field.value}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            error={errors.orgName?.message}
                        />
                    )}
                />

                <YStack>
                    <Controller
                        name="state"
                        control={control}
                        render={({ field }) => (
                            <CustomSelect
                                label="State *"
                                editMode
                                value={field.value}
                                onValueChange={field.onChange}
                                placeholder="Select state..."
                                error={!!errors.state}
                            >
                                {states.map(s => (
                                    <Option key={s} value={s}>
                                        {s}
                                    </Option>
                                ))}
                            </CustomSelect>
                        )}
                    />
                    {errors.state && (
                        <SizableText size="$2" color="$red10" marginTop="$1">
                            {errors.state.message}
                        </SizableText>
                    )}
                </YStack>

                <Controller
                    name="tradingName"
                    control={control}
                    render={({ field }) => (
                        <Input
                            label="Trading Name *"
                            editMode
                            value={field.value}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            error={errors.tradingName?.message}
                        />
                    )}
                />

                <XStack gap="$4">
                    <Controller
                        name="abn"
                        control={control}
                        render={({ field }) => (
                            <Input
                                label="ABN *"
                                editMode
                                value={field.value}
                                onChange={field.onChange}
                                onBlur={field.onBlur}
                                error={errors.abn?.message}
                                formControlProps={{ flex: 1 }}
                            />
                        )}
                    />
                    <Controller
                        name="acn"
                        control={control}
                        render={({ field }) => (
                            <Input
                                label="ACN"
                                editMode
                                value={field.value}
                                onChange={field.onChange}
                                onBlur={field.onBlur}
                                formControlProps={{ flex: 1 }}
                            />
                        )}
                    />
                </XStack>

                <XStack gap="$4">
                    <Controller
                        name="licenceNumber"
                        control={control}
                        render={({ field }) => (
                            <Input
                                label="Licence Number *"
                                editMode
                                value={field.value}
                                onChange={field.onChange}
                                onBlur={field.onBlur}
                                error={errors.licenceNumber?.message}
                                formControlProps={{ flex: 1 }}
                            />
                        )}
                    />
                    <Controller
                        name="licenceExpiry"
                        control={control}
                        render={({ field }) => (
                            <YStack flex={1}>
                                <DatePicker
                                    label="Licence Expiry"
                                    value={field.value || null}
                                    onChange={val => field.onChange(val ?? '')}
                                    onBlur={field.onBlur}
                                />
                            </YStack>
                        )}
                    />
                </XStack>

                <XStack gap="$4">
                    <Controller
                        name="licenseeName"
                        control={control}
                        render={({ field }) => (
                            <Input
                                label="Licensee Name"
                                editMode
                                value={field.value}
                                onChange={field.onChange}
                                onBlur={field.onBlur}
                                formControlProps={{ flex: 1 }}
                            />
                        )}
                    />
                    <Controller
                        name="phone"
                        control={control}
                        render={({ field }) => (
                            <Input
                                label="Phone *"
                                editMode
                                value={field.value}
                                onChange={field.onChange}
                                onBlur={field.onBlur}
                                error={errors.phone?.message}
                                formControlProps={{ flex: 1 }}
                            />
                        )}
                    />
                </XStack>

                <XStack gap="$4">
                    <Controller
                        name="mobileNumber"
                        control={control}
                        render={({ field }) => (
                            <Input
                                label="Mobile Number"
                                editMode
                                value={field.value}
                                onChange={field.onChange}
                                onBlur={field.onBlur}
                                formControlProps={{ flex: 1 }}
                            />
                        )}
                    />
                    <Controller
                        name="fax"
                        control={control}
                        render={({ field }) => (
                            <Input
                                label="Fax Number"
                                editMode
                                value={field.value}
                                onChange={field.onChange}
                                onBlur={field.onBlur}
                                formControlProps={{ flex: 1 }}
                            />
                        )}
                    />
                </XStack>

                <Controller
                    name="orgEmail"
                    control={control}
                    render={({ field }) => (
                        <Input
                            label="Organisation Email *"
                            editMode
                            type="email"
                            value={field.value}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            error={errors.orgEmail?.message}
                        />
                    )}
                />

                <Controller
                    name="website"
                    control={control}
                    render={({ field }) => (
                        <Input
                            label="Website"
                            editMode
                            placeholder="https://"
                            value={field.value}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                        />
                    )}
                />

                <Button
                    theme="primary"
                    // @ts-ignore - HTML form submit
                    type="submit"
                    loading={isSubmitting || createOrgMutation.isPending}
                    animation="quick"
                    hoverStyle={{ backgroundColor: '$backgroundHover' }}
                    pressStyle={{ backgroundColor: '$backgroundPress', opacity: 0.85 }}
                >
                    Continue to Payment
                </Button>
            </YStack>
        </form>
    )
}
